import { useEffect, useRef, useState } from 'react';
import { Camera, Check, ImageUp, Loader2, Mic, MicOff, RefreshCw, SwitchCamera, Upload, Video, X } from 'lucide-react';
import { usePartyUi } from './context';
import { pickRecorderMime } from './recorder';

/**
 * Downscales and re-encodes a photo as JPEG in the browser (fixes EXIF rotation, strips metadata such as
 * GPS, and keeps uploads small on slow mobile data). Large originals never leave the phone.
 */
export async function compressImage(file: Blob, maxDim = 1600, quality = 0.85): Promise<Blob> {
  let source: ImageBitmap | HTMLImageElement;
  try {
    source = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    source = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('This photo format is not supported. Try a JPEG or PNG.'));
      img.src = URL.createObjectURL(file);
    });
  }
  const w = 'naturalWidth' in source ? source.naturalWidth : source.width;
  const h = 'naturalHeight' in source ? source.naturalHeight : source.height;
  const scale = Math.min(1, maxDim / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not process the photo');
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', quality));
  if (!blob) throw new Error('Could not process the photo');
  return blob;
}

export function videoDuration(blob: Blob): Promise<number> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      // Chrome reports Infinity for MediaRecorder WebM until seeked; treat unknown as OK (client recorded it).
      resolve(Number.isFinite(v.duration) ? v.duration : 0);
      URL.revokeObjectURL(v.src);
    };
    v.onerror = () => resolve(0);
    v.src = URL.createObjectURL(blob);
  });
}

export type UploadPhase = { state: 'idle' } | { state: 'uploading'; progress: number } | { state: 'error'; message: string } | { state: 'done' };

/**
 * Upload + submit with the platform's guarantees: announce the upload (so a slow upload that started in time
 * gets the grace period), upload with progress, then submit the server-issued media id. Retry is safe.
 */
export function useMediaSubmit() {
  const { act, upload, sound } = usePartyUi();
  const [phase, setPhase] = useState<UploadPhase>({ state: 'idle' });
  async function submit(blob: Blob, kind: 'image' | 'video', action: Record<string, unknown>) {
    setPhase({ state: 'uploading', progress: 0 });
    void act({ type: 'upload_intent' });
    const up = await upload(blob, kind, (p) => setPhase({ state: 'uploading', progress: p }));
    if ('error' in up) {
      sound.play('error');
      setPhase({ state: 'error', message: up.error });
      return false;
    }
    const r = await act({ ...action, mediaId: up.mediaId, consent: true });
    if (!r.ok) {
      sound.play('error');
      setPhase({ state: 'error', message: r.error ?? 'Could not submit' });
      return false;
    }
    sound.play('submit');
    setPhase({ state: 'done' });
    return true;
  }
  return { phase, submit, reset: () => setPhase({ state: 'idle' }) };
}

export function UploadStatus({ phase, onRetry }: { phase: UploadPhase; onRetry?: () => void }) {
  if (phase.state === 'uploading')
    return (
      <div className="w-full" role="status" aria-live="polite">
        <div className="flex items-center gap-2 text-sm text-mute mb-1">
          <Loader2 size={16} className="animate-spin" /> Uploading… {Math.round(phase.progress * 100)}%
        </div>
        <div className="h-2 rounded-full bg-ink-3 overflow-hidden">
          <div className="h-full bg-[var(--accent)] transition-[width]" style={{ width: `${phase.progress * 100}%` }} />
        </div>
      </div>
    );
  if (phase.state === 'error')
    return (
      <div className="w-full rounded-2xl bg-bad/15 border border-bad/50 p-3 text-sm flex items-center gap-3" role="alert">
        <span className="flex-1">{phase.message}</span>
        {onRetry && (
          <button className="btn btn-sm btn-secondary" onClick={onRetry}>
            <RefreshCw size={16} /> Retry
          </button>
        )}
      </div>
    );
  return null;
}

/**
 * Agreeing to share photos and clips once per party, in the lobby (MediaConsentCard), instead of ticking a box
 * on every submission. Remembered on this device for that party's code; games skip their ConsentCheck when
 * it's given. (Every submission still carries consent: true, as before.)
 */
const consentKey = (code: string) => `gmp.mediaConsent.${code}`;
const consentListeners = new Set<() => void>();
function readConsent(code: string): boolean {
  try {
    return localStorage.getItem(consentKey(code)) === '1';
  } catch {
    return memoryConsent.has(code);
  }
}
const memoryConsent = new Set<string>();
export function setMediaConsent(code: string, given: boolean) {
  try {
    if (given) localStorage.setItem(consentKey(code), '1');
    else localStorage.removeItem(consentKey(code));
  } catch {
    /* storage blocked: this page load only */
  }
  if (given) memoryConsent.add(code);
  else memoryConsent.delete(code);
  for (const l of consentListeners) l();
}
/** Whether this device agreed to share photos and clips in the party with this code. */
export function useMediaConsent(code: string | null | undefined): boolean {
  const [, setTick] = useState(0);
  useEffect(() => {
    const l = () => setTick((t) => t + 1);
    consentListeners.add(l);
    return () => void consentListeners.delete(l);
  }, []);
  return !!code && (memoryConsent.has(code) || readConsent(code));
}

/** The lobby card for a game that uses photos or clips: agree once for the whole party. */
export function MediaConsentCard({ code, game, onAgree }: { code: string; game: string; onAgree?: () => void }) {
  const given = useMediaConsent(code);
  if (given)
    return (
      <div className="card p-3 flex items-center gap-3 text-sm" data-testid="media-consent-given">
        <Check size={18} className="text-good shrink-0" aria-hidden />
        <span className="flex-1">You agreed to share your photos and clips with this party.</span>
        <button className="btn btn-ghost btn-sm" onClick={() => setMediaConsent(code, false)}>Undo</button>
      </div>
    );
  return (
    <div className="card p-4 grid gap-3 border-2 border-[var(--accent)]" data-testid="media-consent">
      <div className="flex items-center gap-2 font-display font-black text-lg"><Camera size={20} aria-hidden /> {game} uses your camera</div>
      <p className="text-sm text-mute">
        The photos and clips you take are shared with the players in this party only. They are never public and are deleted automatically after the party.{' '}
        <a href="/privacy" target="_blank" rel="noreferrer" className="underline underline-offset-2">Privacy</a>
      </p>
      <button className="btn" onClick={() => { setMediaConsent(code, true); onAgree?.(); }} data-testid="media-consent-agree">
        <Check size={18} aria-hidden /> I agree, let's play
      </button>
    </div>
  );
}

export function ConsentCheck({ checked, onChange, what }: { checked: boolean; onChange: (v: boolean) => void; what: string }) {
  return (
    <label className="flex items-start gap-3 text-sm text-mute cursor-pointer select-none">
      <input type="checkbox" className="mt-0.5 size-5 accent-[var(--accent)]" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        Share this {what} with the players in this party. It is never public and is deleted automatically after the party.{' '}
        <a href="/privacy" target="_blank" rel="noreferrer" className="underline underline-offset-2">Privacy</a>
      </span>
    </label>
  );
}

/**
 * Photo capture: the phone's own camera via file capture (works on iOS Safari and Android Chrome without
 * permissions prompts), or the photo library when the host allows it. Desktop falls back to a file picker.
 */
export function PhotoPicker({ allowLibrary, onPicked, disabled }: { allowLibrary: boolean; onPicked: (blob: Blob, previewUrl: string) => void; disabled?: boolean }) {
  const cam = useRef<HTMLInputElement>(null);
  const lib = useRef<HTMLInputElement>(null);
  const { sound } = usePartyUi();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function handle(files: FileList | null) {
    const f = files?.[0];
    if (!f) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await compressImage(f);
      sound.play('shutter');
      onPicked(blob, URL.createObjectURL(blob));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      if (cam.current) cam.current.value = '';
      if (lib.current) lib.current.value = '';
    }
  }
  return (
    <div className="flex flex-col gap-3 w-full">
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handle(e.target.files)} data-testid="photo-camera-input" />
      <input ref={lib} type="file" accept="image/*" className="hidden" onChange={(e) => handle(e.target.files)} data-testid="photo-library-input" />
      <button className="btn w-full text-xl min-h-16" disabled={disabled || busy} onClick={() => cam.current?.click()}>
        {busy ? <Loader2 className="animate-spin" /> : <Camera />} Open Camera
      </button>
      {allowLibrary && (
        <button className="btn btn-secondary w-full" disabled={disabled || busy} onClick={() => lib.current?.click()}>
          <ImageUp size={20} /> Choose Photo
        </button>
      )}
      {error && <p className="text-bad text-sm" role="alert">{error}</p>}
    </div>
  );
}

function supportedMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  return pickRecorderMime((m) => MediaRecorder.isTypeSupported(m));
}

/**
 * Short performance recorder. Live preview, visible countdown, auto-stop at the limit, front/rear switch,
 * mute, playback and retake. Uses feature detection for the recording format and falls back to the
 * phone's native video capture (file input) when recording in the page is not possible.
 */
export function VideoRecorder({ maxSeconds, onRecorded, onPhotoInstead }: { maxSeconds: number; onRecorded: (blob: Blob, url: string) => void; onPhotoInstead: () => void }) {
  const { sound } = usePartyUi();
  const preview = useRef<HTMLVideoElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const [facing, setFacing] = useState<'user' | 'environment'>('user');
  const [muted, setMuted] = useState(false);
  const [status, setStatus] = useState<'starting' | 'ready' | 'countdown' | 'recording' | 'unsupported' | 'denied'>('starting');
  const [count, setCount] = useState(3);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const mime = useRef(supportedMime());

  useEffect(() => {
    let cancelled = false;
    async function start() {
      if (!navigator.mediaDevices?.getUserMedia || !mime.current) {
        setStatus('unsupported');
        return;
      }
      setStatus('starting');
      try {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: { ideal: 720 }, height: { ideal: 1280 }, frameRate: { ideal: 30 } },
          audio: !muted,
        });
        if (cancelled) return stream.getTracks().forEach((t) => t.stop());
        streamRef.current = stream;
        if (preview.current) {
          preview.current.srcObject = stream;
          await preview.current.play().catch(() => undefined);
        }
        setStatus('ready');
      } catch (e) {
        setStatus((e as DOMException).name === 'NotAllowedError' ? 'denied' : 'unsupported');
      }
    }
    void start();
    return () => {
      cancelled = true;
    };
  }, [facing, muted]);

  useEffect(() => () => streamRef.current?.getTracks().forEach((t) => t.stop()), []);

  function beginCountdown() {
    sound.play('select');
    setStatus('countdown');
    let n = 3;
    setCount(n);
    const id = window.setInterval(() => {
      n--;
      if (n <= 0) {
        clearInterval(id);
        record();
      } else {
        setCount(n);
        sound.play('tap');
      }
    }, 700);
  }

  function record() {
    const stream = streamRef.current;
    if (!stream || !mime.current) return;
    const chunks: Blob[] = [];
    const rec = new MediaRecorder(stream, { mimeType: mime.current, videoBitsPerSecond: 1_500_000 });
    recRef.current = rec;
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    rec.onstop = () => {
      const type = mime.current!.split(';')[0]!;
      const blob = new Blob(chunks, { type });
      streamRef.current?.getTracks().forEach((t) => t.stop());
      onRecorded(blob, URL.createObjectURL(blob));
    };
    rec.start(250);
    sound.play('shutter');
    setStatus('recording');
    const started = performance.now();
    const id = window.setInterval(() => {
      const s = (performance.now() - started) / 1000;
      setElapsed(s);
      if (s >= maxSeconds) {
        clearInterval(id);
        if (rec.state === 'recording') rec.stop();
      }
    }, 100);
    rec.addEventListener('stop', () => clearInterval(id));
  }

  function stop() {
    if (recRef.current?.state === 'recording') recRef.current.stop();
  }

  async function pickFile(files: FileList | null) {
    const f = files?.[0];
    if (!f) return;
    const d = await videoDuration(f);
    if (d > maxSeconds + 1.5) {
      setError(`That clip is ${Math.round(d)} s. Keep it to ${maxSeconds} seconds.`);
      return;
    }
    if (f.size > 40 * 1024 * 1024) {
      setError('That video is too large. Record a shorter clip.');
      return;
    }
    onRecorded(f, URL.createObjectURL(f));
  }

  const fallback = (
    <>
      <input ref={fileInput} type="file" accept="video/*" capture="user" className="hidden" onChange={(e) => pickFile(e.target.files)} data-testid="video-file-input" />
      <button className="btn w-full" onClick={() => fileInput.current?.click()}>
        <Video /> Record with phone camera
      </button>
    </>
  );

  return (
    <div className="flex flex-col gap-3 w-full">
      {status === 'unsupported' || status === 'denied' ? (
        <div className="card p-4 text-center flex flex-col gap-3">
          <p className="text-mute">
            {status === 'denied' ? 'Camera access was blocked. You can still record with your phone camera or strike a photo pose.' : 'This browser cannot record in the page. Use your phone camera instead.'}
          </p>
          {fallback}
        </div>
      ) : (
        <div className="relative rounded-3xl overflow-hidden bg-black aspect-[3/4] max-h-[52vh] mx-auto w-full">
          <video ref={preview} playsInline muted autoPlay className={`w-full h-full object-cover ${facing === 'user' ? '-scale-x-100' : ''}`} />
          {status === 'countdown' && (
            <div className="absolute inset-0 grid place-items-center bg-black/40">
              <span key={count} className="font-display font-black text-8xl animate-pop">{count}</span>
            </div>
          )}
          {status === 'recording' && (
            <div className="absolute top-3 inset-x-3 flex items-center gap-2">
              <span className="size-3 rounded-full bg-bad animate-pulse" />
              <div className="flex-1 h-2 rounded-full bg-white/25 overflow-hidden">
                <div className="h-full bg-bad" style={{ width: `${Math.min(100, (elapsed / maxSeconds) * 100)}%` }} />
              </div>
              <span className="font-display font-black tabular-nums">{Math.max(0, Math.ceil(maxSeconds - elapsed))}s</span>
            </div>
          )}
          {status !== 'recording' && status !== 'countdown' && (
            <div className="absolute top-3 right-3 flex gap-2">
              <button className="btn btn-sm btn-secondary" aria-label="Switch camera" onClick={() => setFacing((f) => (f === 'user' ? 'environment' : 'user'))}>
                <SwitchCamera size={18} />
              </button>
              <button className="btn btn-sm btn-secondary" aria-label={muted ? 'Turn sound on' : 'Mute recording'} onClick={() => setMuted((m) => !m)}>
                {muted ? <MicOff size={18} /> : <Mic size={18} />}
              </button>
            </div>
          )}
        </div>
      )}
      {status === 'ready' && (
        <button className="btn w-full text-xl min-h-16" onClick={beginCountdown} data-testid="record-start">
          <span className="size-4 rounded-full bg-bad ring-4 ring-white/40" /> Record {maxSeconds}s
        </button>
      )}
      {status === 'recording' && (
        <button className="btn btn-secondary w-full min-h-16" onClick={stop}>
          <X /> Stop
        </button>
      )}
      {status === 'starting' && <p className="text-center text-mute text-sm">Starting camera…</p>}
      {status !== 'unsupported' && status !== 'denied' && status !== 'recording' && <div className="hidden">{fallback}</div>}
      <button className="btn btn-ghost btn-sm self-center" onClick={onPhotoInstead}>
        <Camera size={16} /> Strike a photo pose instead
      </button>
      {status === 'ready' && (
        <button className="btn btn-ghost btn-sm self-center" onClick={() => fileInput.current?.click()}>
          <Upload size={16} /> Upload a clip
        </button>
      )}
      {error && <p className="text-bad text-sm text-center" role="alert">{error}</p>}
    </div>
  );
}

export function ReadyBadge({ text }: { text: string }) {
  return (
    <div className="flex items-center justify-center gap-2 text-good font-bold">
      <Check size={20} /> {text}
    </div>
  );
}
